import classNames from 'classnames';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import Container from '../../../containers';
import { JSX } from 'react';
import { NavigationIcon } from '@tymbe/ui-icons';

// Sidebar Item Type
type SidebarItem = {
  id: string;
  label: string;
  content: JSX.Element;
};

// Sidebar Item Component
const SidebarItem = ({
  item,
  isActive,
  onClick,
}: {
  item: SidebarItem;
  isActive: boolean;
  onClick: () => void;
}) => {
  return (
    <div
      onClick={onClick}
      className={classNames(
        'px-3 py-3 text-body-2-md font-normal cursor-pointer hover:bg-secondary-transparent-5 transition-colors rounded-lg',
        isActive ? 'bg-secondary-transparent-10' : ''
      )}
    >
      {item.label}
    </div>
  );
};

// Content Display Component
export const ContentDisplay = ({ item }: { item: SidebarItem }) => {
  const navigate = useNavigate();
  const handleBack = () => {
    navigate('/administration/help', { replace: true });
  };
  return (
    <div className="px-4 max-w-[1100px] mx-auto">
      {handleBack && (
        <div
          onClick={handleBack}
          className="flex items-center py-6 cursor-pointer md:hidden"
        >
          <NavigationIcon name="west" className="mr-2 size-5" />
          <span className="text-heading-3-md text-secondary-600">
            {item.label}
          </span>
        </div>
      )}
      <span className="text-heading-3-md text-secondary-600 hidden md:flex py-6">
        {item.label}
      </span>
      <div>{item.content}</div>
    </div>
  );
};

export const sidebarItems: SidebarItem[] = [
  {
    id: 'calendar_overview',
    label: 'Kalendář - přehled',
    content: (
      <div>
        <iframe className='w-full aspect-video' src="https://www.youtube.com/embed/afNKTkQcgSQ?si=hPSHYkfc85GozK7g" title="YouTube video player" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" referrerPolicy="strict-origin-when-cross-origin" allowFullScreen></iframe>
        <div className='text-heading-4-md py-5'>Shrnutí</div>
        Jak efektivně pracovat s kalendářem? V tomto videu zjistíte, jak snadno
        filtrovat mezi pozicemi, sledovat na klik obsazenost, editovat již
        vytvořené směny, mazat ty neobsazené, nebo jak se dostat na profil
        Tymbera. Všechno přehledně na jednom místě – ať víte, co se kdy děje.
      </div>
    ),
  },
  {
    id: 'new-shifts',
    label: 'Jak vypsat nové směny?',
    content: (
      <div>
        <iframe className='w-full aspect-video' src="https://www.youtube.com/embed/s4rVIq28t3s?si=VNPfSb-kSddv7T8J" title="YouTube video player" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" referrerPolicy="strict-origin-when-cross-origin" allowFullScreen></iframe>
        <div className='text-heading-4-md py-5'>Shrnutí</div>
        Ve videu najdete celý proces zadávání nové směny – od základů, jako je
        výběr pozice, data a času, zadání počtu lidí a nouzového kontaktu, až po
        nastavení detailních filtrů, nebo práci s pozvánkami. Ukážeme si, jak
        směny během procesu editovat, přidat další pozice i jak je jednoduše
        publikovat. Rychlé, přehledné a bez zbytečných komplikací.
      </div>
    ),
  },
  {
    id: 'attendance_confirmation',
    label: 'Jak potvrdit docházku?',
    content: (
      <div>
        <iframe className='w-full aspect-video' src="https://www.youtube.com/embed/kcEWBKx_mRE?si=legNmE1JOLPQcOk-" title="YouTube video player" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" referrerPolicy="strict-origin-when-cross-origin" allowFullScreen></iframe>
        <div className='text-heading-4-md py-5'>Shrnutí</div>
        Kruh se uzavírá potvrzením docházky - Jak na ní? Ukážeme Vám, jak rychle
        najít směny čekající na potvrzení, upravit časy příchodů / odchodů, nebo
        délku směny, přidat zpětnou vazbu nebo označit absenci.
      </div>
    ),
  },
];

// Main Sidebar Component
const Sidebar = ({ children }: { children: React.ReactNode }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const sidebarItemNotFound: SidebarItem = {
    id: 'not-found',
    label: 'Nenalezeno',
    content: <div>Spatny odkaz</div>,
  };

  const handleItemClick = (itemId: string) => {
    navigate(`/administration/help/${itemId}`, { replace: true });
  };

  const selectedItem =
    sidebarItems.find((item) => location.pathname.includes(item.id)) ??
    sidebarItemNotFound;

  return (
    <div className="h-full">
      {/* Mobile View */}
      <div className="md:hidden -mx-4">
        <div className="w-full bg-white">
          <div className="px-4 py-6 border-b border-secondary-transparent-5 border-[1px]">
            <h1 className="text-heading-3-md font-semibold text-secondary-600">
              Navigation
            </h1>
          </div>
          {selectedItem.id === sidebarItemNotFound.id ? (
            <div className="py-4 px-2">
              {sidebarItems.map((item) => (
                <SidebarItem
                  key={item.id}
                  item={item}
                  isActive={selectedItem.id === item.id}
                  onClick={() => handleItemClick(item.id)}
                />
              ))}
            </div>
          ) : (
            <div className="w-full bg-gray-50">{children}</div>
          )}
        </div>
      </div>

      {/* Desktop View */}
      <div className="hidden md:flex h-full flex-col -mx-4">
        <div className="px-4 py-11 border-b border-secondary-transparent-5 border-[1px]">
          <h1 className="text-heading-3-md font-semibold text-secondary-600">
            Nápověda
          </h1>
        </div>
        <div className="flex h-full">
          <div className="w-64 bg-white border-r border-secondary-transparent-5 border-[1px]">
            <div className="py-2 px-2">
              {sidebarItems.map((item) => (
                <SidebarItem
                  key={item.id}
                  item={item}
                  isActive={selectedItem.id === item.id}
                  onClick={() => handleItemClick(item.id)}
                />
              ))}
            </div>
          </div>
          <div className="flex-1 bg-gray-50">{children}</div>
        </div>
      </div>
    </div>
  );
};

const HelpPage = () => {
  return (
    <Container>
      <Sidebar>
        <Outlet />
      </Sidebar>
    </Container>
  );
};

export default HelpPage;
